import React, { Ref, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import styles from "./FormCard.module.scss";
import Input from "@/common/components/Input";
import {
  ErrorData,
  FormFields,
  FormResponse,
} from "../../helpers/CreatorForm.types";
import Image from "next/image";
import { Images } from "@/utils/constants/imageRelativePaths";
import { CustomDatePicker } from "chingari-components";
import { Value } from "../../helpers/CreatorForm.types";
import { AstroUserData, GeoLocation } from "../../Enum";
import useDebounce from "@/customhooks/useDebounce";
import { fetchLocationData, fetchTimeZone } from "@/services/astro.service";
import useOnClickOutside from "../../../../customhooks/useOnClickOutside";
import CustomRadioDropdown from "@/common/components/CustomRadioDropDown/CustomRadioDropdown";
import { getFormatDateFromPrediction2 } from "../../helper";
import useAppContext from "@/customhooks/useAppContext";
import { formatDate } from "@/utils/helper";

interface FormCardProps {
  gender?: string;
  initialData: FormResponse;
  submit: boolean;
  onSubmit: (formName: string, data: AstroUserData | null) => void;
  onNameClick?: () => void;
  formFields: FormFields[];
  title?: string;
}

const FormCard = ({
  gender,
  initialData,
  submit,
  onSubmit,
  onNameClick,
  formFields,
  title,
}: FormCardProps) => {
  const router = useRouter();
  const context = useAppContext();
  const token = context.auth.token ?? "";
  const [init, setInit] = useState(false);
  const [formData, setFormData] = useState<FormResponse>(initialData || {});
  const [errors, setErrors] = useState<ErrorData>({});
  const [date, setDate] = useState<Value>(initialData.DOB as Value);
  const [isCardVisible, setCardVisible] = useState<boolean>(false);
  const [selected, setSelected] = useState(false);
  const [selectedCity, setSelectedCity] = useState<GeoLocation>();
  const [timeZone, setTimeZone] = useState("");
  const [searchOptions, setSearchOptions] = useState<GeoLocation[]>([]);
  const debouncedValue = useDebounce<string>(
    formData?.place_of_birth as string
  );
  const ref = useRef(null);
  useOnClickOutside(ref, onClearInput);

  function onClearInput() {
    setFormData((prev) => ({ ...prev, ["place_of_birth"]: "" }));
    setSearchOptions([]);
  }

  function setLocationData(location: GeoLocation) {
    setSelected(true);
    setSelectedCity(location);
    setFormData((prev) => ({
      ...prev,
      ["place_of_birth"]: location?.place_name,
    }));
    setSearchOptions([]);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleShowCalendar = (name: string) => {
    name === "DOB"
      ? setCardVisible(!isCardVisible)
      : setCardVisible(isCardVisible);
  };

  const getLocationData = async () => {
    const locationData = await fetchLocationData(debouncedValue?.trim(), token);
    setSearchOptions(locationData?.geonames ?? []);
    if (!init) {
      const location = locationData?.geonames?.find(
        ({ place_name }) =>
          place_name?.toLowerCase() ===
          (formData?.place_of_birth as string)?.toLowerCase()
      );
      if (location && initialData?.place_of_birth) {
        setLocationData(location);
      }
      setInit(true);
    }
  };

  const getTimeZone = async () => {
    const timezoneData = await fetchTimeZone({
      date: new Date().toString(),
      latitude: selectedCity?.latitude ?? "",
      longitude: selectedCity?.longitude ?? "",
      token,
    });
    if (timezoneData?.timezone) setTimeZone(timezoneData?.timezone);
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      DOB: typeof date === "object" ? formatDate(date as Date, "-") : date,
    }));
  }, [date]);

  useEffect(() => {
    if (debouncedValue?.length > 2 && !selected) {
      void getLocationData();
    } else {
      setSelected(false);
      setSearchOptions([]);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (selectedCity?.country_code) {
      void getTimeZone();
    }
  }, [selectedCity?.place_name]);

  useEffect(() => {
    if (Object.keys(initialData)?.length > 0) {
      setFormData(initialData);
      setSelectedCity(undefined);
    }
  }, [initialData]);

  useEffect(() => {
    if (submit) {
      let newErrors: ErrorData = {};

      if (formData?.DOB && formData?.DOB > formatDate(new Date(), "-")) {
        newErrors.DOB = "Incorrect D.O.B";
      }

      formFields?.forEach(({ name }) => {
        if (!formData?.[name]?.toString()?.trim()) {
          newErrors[name] = "Required";
        }
        if (name === "place_of_birth") {
          if (!selectedCity || !timeZone) {
            newErrors.place_of_birth = "Error fetching location";
          }
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        onSubmit(gender === "Male" ? "maleData" : "femaleData", null);
      } else {
        setErrors({});
        let finalDate = getFormatDateFromPrediction2(formData?.DOB as string);
        if (formData?.time_of_birth) {
          const [hours, min] = String(formData?.time_of_birth)?.split(":");
          finalDate.setHours(Number(hours));
          finalDate.setMinutes(Number(min));
        }

        let userData = {
          name: formData?.name as string,
          date: finalDate.toString(),
          lat: selectedCity?.latitude ?? "",
          long: selectedCity?.longitude ?? "",
          tzone: timeZone,
          dob: formData?.DOB as string,
          tob: formData?.time_of_birth as string,
          pob: formData?.place_of_birth as string,
          gender: (gender ?? (formData?.gender as string))?.slice(0, 1),
          varshaphal_year: formData?.varshaphal_year,
          zodiac: (formData?.zodiac as string)?.toLowerCase(),
          city: formData?.city as string,
        };
        onSubmit(gender === "Male" ? "maleData" : "femaleData", userData);
      }
    }
  }, [submit]);

  return (
    <div className={styles.formCard}>
      {title && <h4>{title}</h4>}
      {gender && !title && (
        <h4>{gender === "Male" ? "Boy" : "Girl"} Details</h4>
      )}
      <div className={styles.formWrapper}>
        {formFields.map(({ id, label, name, required, type, options }) => (
          <div className={styles.formField} key={id}>
            {type === "radio" && (
              <>
                <CustomRadioDropdown
                  label={required ? `${label} *` : label}
                  options={options || []}
                  onSelect={({ value }) => handleSelectChange(name, value)}
                  value={formData[name] as string}
                />
                <p className={styles.error}>{errors[name]}</p>
              </>
            )}
            {type !== "date" && type !== "radio" && (
              <>
                <Input
                  label={required ? `${label} *` : label}
                  type={type}
                  name={name}
                  value={formData?.[name] as string}
                  onChange={handleInputChange}
                  error={errors[name]}
                />
                {name === "name" && onNameClick && (
                  <Image
                    className={styles.inputImg}
                    src={Images.contacts}
                    alt="contacts"
                    height={20}
                    width={20}
                    onClick={onNameClick}
                  />
                )}
                {name === "place_of_birth" && searchOptions?.length > 0 && (
                  <div className={styles.dropdown} ref={ref}>
                    {searchOptions?.map((location, index) => (
                      <div
                        className={styles.dropdownItem}
                        key={index}
                        onClick={() => {
                          if (location?.country_code) {
                            setLocationData(location);
                          }
                        }}
                      >
                        {location?.place_name}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {type === "date" && (
              <div className={styles.calendar}>
                <Input
                  type={type}
                  label={required ? `${label} *` : label}
                  name={name}
                  value={formData?.[name] as string}
                  onChange={handleInputChange}
                  error={errors[name]}
                />
                {/* <Image
                  className={styles.inputImg}
                  src={Images.calendar}
                  alt="calendar"
                  height={16}
                  width={16}
                  onClick={() => handleShowCalendar(name)}
                /> */}
                {name === "DOB" && isCardVisible && (
                  <CustomDatePicker
                    userDate={date}
                    setUserDate={setDate}
                    isCardVisible={isCardVisible}
                    setCardVisible={setCardVisible}
                    closeIcon={Images.close}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FormCard;
